import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TextField, Button, IconButton, Typography } from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Clear as ClearIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

type UploadedFile = {
  file: File;
  preview: string;
  name: string;
  type: 'image' | 'video' | 'other';
};

type Props = {
  value: string | null;
  label?: string;
  required?: boolean;
  helperText?: string;
  onChange: (value: string | null, error: string | null) => void;
  onChangeUpload?: (file: File) => Promise<string>;
  classes?: {
    root?: string;
    input?: string;
    label?: string;
    helperText?: string;
    uploadButton?: string;
    previewContainer?: string;
    previewImage?: string;
    clearButton?: string;
  };
  config?: {
    urlLabel?: string;
    uploadButtonLabel?: string;
    fileDialogTitle?: string;
    fileDialogAccept?: string;
    maxFileSize?: number;
  };
};

export const UrlFileUploadField: React.FC<Props> = ({
  value,
  label,
  required,
  helperText,
  onChange,
  onChangeUpload,
  classes = {
    root: 'mb-4 text-mainText',
    label: 'text-medium',
    input: 'bg-gray-50 text-small',
    helperText: 'text-small italic',
    uploadButton: 'bg-blue-500 hover:bg-blue-600 text-white',
    previewContainer:
      'flex flex-col items-center gap-2 p-3 border rounded bg-gray-50',
    previewImage: 'w-20 h-20 object-cover rounded border',
    clearButton: 'text-red-500 hover:text-red-700',
  },
  config = {
    urlLabel: 'URL',
    uploadButtonLabel: 'Upload File',
    fileDialogTitle: 'Select File',
    fileDialogAccept: 'image/*,video/*',
    maxFileSize: 10,
  },
}) => {
  const [urlInput, setUrlInput] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);
  const [videoUrlPreview, setVideoUrlPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current) {
      setUrlInput(value || '');
      if (!value || !value.includes('data:')) {
        setUploadedFile(null);
      }
      setImageLoadError(false);
    }
    isInternalChange.current = false;
  }, [value]);

  const validateUrl = useCallback((url: string): boolean => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const validateFile = useCallback(
    (file: File): string | null => {
      const maxSize = (config.maxFileSize || 10) * 1024 * 1024;
      if (file.size > maxSize) {
        return `File size must be less than ${config.maxFileSize}MB`;
      }
      return null;
    },
    [config.maxFileSize]
  );

  const captureVideoFrameFromUrl = useCallback(
    (url: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        video.crossOrigin = 'anonymous';
        video.onloadedmetadata = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          video.onseeked = () => {
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL('image/jpeg', 0.8));
            }
          };

          video.currentTime = 0.1;
        };

        video.onerror = () =>
          reject(new Error('Failed to load video from URL'));
        video.src = url;
      });
    },
    []
  );

  const handleUrlChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const url = e.target.value;
      setUrlInput(url);
      isInternalChange.current = true;

      if (!url) {
        setError(null);
        setVideoUrlPreview(null);
        setUploadedFile(null);
        onChange(null, null);
        return;
      }

      if (!validateUrl(url)) {
        const message = 'Please enter a valid URL';
        setError(message);
        setVideoUrlPreview(null);
        onChange(null, message);
        return;
      }

      setError(null);
      onChange(url, null);

      if (
        url &&
        typeof url === 'string' &&
        url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i)
      ) {
        try {
          const preview = await captureVideoFrameFromUrl(url);
          setVideoUrlPreview(preview);
        } catch (error) {
          console.warn('Failed to capture video frame from URL:', error);
          setVideoUrlPreview(null);
        }
      } else {
        setVideoUrlPreview(null);
      }
    },
    [onChange, validateUrl, captureVideoFrameFromUrl]
  );

  const getFileType = useCallback((file: File): 'image' | 'video' | 'other' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'other';
  }, []);

  const getFileIcon = useCallback(
    (fileName: string, fileType?: string): React.ReactElement => {
      const extension = fileName.split('.').pop()?.toLowerCase();

      if (
        fileType?.startsWith('image/') ||
        extension?.match(/^(jpg|jpeg|png|gif|webp|svg|bmp)$/)
      ) {
        return <ImageIcon sx={{ fontSize: 40, color: '#666' }} />;
      }

      if (
        fileType?.startsWith('video/') ||
        extension?.match(/^(mp4|webm|ogg|mov|avi|mkv|wmv|flv)$/)
      ) {
        return <VideoIcon sx={{ fontSize: 40, color: '#666' }} />;
      }

      return <FileIcon sx={{ fontSize: 40, color: '#666' }} />;
    },
    []
  );

  const captureVideoFrame = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        video.onseeked = () => {
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          }
        };

        video.currentTime = 0.1;
      };

      video.onerror = () => reject(new Error('Failed to load video'));
      video.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFileUpload = useCallback(
    async (file: File) => {
      const fileError = validateFile(file);
      if (fileError) {
        setError(fileError);
        onChange(null, fileError);
        return;
      }

      const fileType = getFileType(file);

      try {
        let preview: string;
        let dataUrl: string;

        if (fileType === 'video') {
          preview = await captureVideoFrame(file);
          dataUrl = await new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onload = e => resolve(e.target?.result as string);
            reader.readAsDataURL(file);
          });
        } else {
          dataUrl = await new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onload = e => resolve(e.target?.result as string);
            reader.readAsDataURL(file);
          });
          preview = dataUrl;
        }

        const uploadedFileData: UploadedFile = {
          file,
          preview,
          name: file.name,
          type: fileType,
        };

        isInternalChange.current = true;
        setUrlInput(dataUrl);
        setError(null);
        setUploadedFile(uploadedFileData);
        onChange(dataUrl, null);
      } catch (error) {
        setError('Failed to process file');
        onChange(null, 'Failed to process file');
      }
    },
    [onChange, validateFile, getFileType, captureVideoFrame]
  );

  const handleExternalFileUpload = useCallback(
    async (file: File) => {
      if (!onChangeUpload) {
        return handleFileUpload(file);
      }

      const fileError = validateFile(file);
      if (fileError) {
        setError(fileError);
        onChange(null, fileError);
        return;
      }

      setIsUploading(true);
      setError(null);

      try {
        const uploadedUrl = await onChangeUpload(file);

        isInternalChange.current = true;
        setUrlInput(uploadedUrl);
        setError(null);
        setUploadedFile(null);
        onChange(uploadedUrl, null);

        if (
          uploadedUrl &&
          typeof uploadedUrl === 'string' &&
          uploadedUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i)
        ) {
          try {
            const preview = await captureVideoFrameFromUrl(uploadedUrl);
            setVideoUrlPreview(preview);
          } catch (error) {
            console.warn(
              'Failed to capture video frame from uploaded URL:',
              error
            );
            setVideoUrlPreview(null);
          }
        } else {
          setVideoUrlPreview(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';
        setError(errorMessage);
        onChange(null, errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [
      onChangeUpload,
      handleFileUpload,
      validateFile,
      onChange,
      captureVideoFrameFromUrl,
    ]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleExternalFileUpload(file);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleExternalFileUpload]
  );

  const handleClear = useCallback(() => {
    isInternalChange.current = true;
    setUrlInput('');
    setError(null);
    setVideoUrlPreview(null);
    setUploadedFile(null);
    setImageLoadError(false);
    onChange(null, null);
  }, [onChange]);

  const urlValue = value || '';
  const isVideoUrl =
    urlValue &&
    typeof urlValue === 'string' &&
    urlValue.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
  const isImageUrl =
    urlValue &&
    typeof urlValue === 'string' &&
    urlValue.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
  const isDataUrl =
    urlValue &&
    typeof urlValue === 'string' &&
    (urlValue.includes('data:image') || urlValue.includes('data:video'));
  const isVideoDataUrl =
    urlValue && typeof urlValue === 'string' && urlValue.includes('data:video');

  const previewSource = uploadedFile?.preview || videoUrlPreview || urlValue;
  const hasValidPreview =
    previewSource &&
    !imageLoadError &&
    (uploadedFile?.preview ||
      videoUrlPreview ||
      isImageUrl ||
      isVideoUrl ||
      isDataUrl);
  const shouldShowPreview = hasValidPreview || urlValue;
  const isVideo =
    uploadedFile?.type === 'video' || isVideoUrl || isVideoDataUrl;
  const hasFile = !!uploadedFile || !!urlValue;

  return (
    <div className={classes.root}>
      <Box display="flex" gap={2} alignItems="flex-start" pb={2}>
        <Box
          className={classes.previewContainer}
          sx={{
            width: 120,
            height: 120,
            position: 'relative',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {shouldShowPreview ? (
            <Box position="relative" width="100%" height="100%">
              {hasValidPreview ? (
                <>
                  {(isVideo || isVideoDataUrl) &&
                  !videoUrlPreview &&
                  !uploadedFile?.preview ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <VideoIcon sx={{ color: 'white', fontSize: 40 }} />
                      <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        sx={{
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <VideoIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </Box>
                  ) : (
                    <img
                      src={previewSource}
                      alt={uploadedFile?.name || 'Preview'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={() => {
                        console.error(
                          'Image preview failed to load:',
                          previewSource
                        );
                        setImageLoadError(true);
                      }}
                      onLoad={() => {
                        console.log(
                          'Image preview loaded successfully:',
                          previewSource
                        );
                        setImageLoadError(false);
                      }}
                    />
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    padding: 1,
                  }}
                >
                  {getFileIcon(
                    uploadedFile?.name || urlValue || 'file',
                    uploadedFile?.file.type
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      color: '#666',
                      wordBreak: 'break-word',
                      lineHeight: 1.2,
                    }}
                  >
                    {uploadedFile?.name ||
                      (urlValue && urlValue.includes('/')
                        ? urlValue.split('/').pop()?.split('?')[0] || 'File'
                        : 'File')}
                  </Typography>
                </Box>
              )}
              {isVideo && (
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VideoIcon sx={{ color: 'white', fontSize: 16 }} />
                </Box>
              )}
              <IconButton
                size="small"
                onClick={handleClear}
                className={classes.clearButton}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={!!urlInput || isUploading}
              sx={{
                fontSize: '0.75rem',
                padding: '4px 8px',
                minWidth: 'auto',
                height: 'auto',
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gap={1} flex={1}>
          <TextField
            className={classes.input}
            fullWidth
            label={`${label} ${required ? '*' : ''}`}
            placeholder="Enter URL..."
            value={urlInput || ''}
            onChange={handleUrlChange}
            error={!!error}
            disabled={isUploading || hasFile}
            variant="outlined"
            helperText={error || helperText}
          />
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={config.fileDialogAccept}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </div>
  );
};
